// "Your first sign-in" — a public page the invitation links to.
//
// The two steps a new person meets before they see anything of ours (choose
// a password, set up a code app) happen on Cognito's pages, which explain
// neither until something fails, and the practice owner who gives up there
// never reaches the product (2026-09-04 UX review, finding 9). This page says
// what will happen, in order, in plain words, with what to do when it does
// not. No sign-in needed to read it; nothing here is about any customer.

import { useEffect, lazy, Suspense } from 'react';
import { Link } from 'react-router-dom';
import { safeReturnTo } from '../auth/cognito';
import { useSurfaceTheme } from '../customer/useSurfaceTheme';
import { useDeviceViewport } from '../portal/useDeviceViewport';
import { PASSKEY_COPY } from '../auth/strings/passkeys';
import { contactPhrase, SIGNED_OUT_WORDS } from '../auth/support';

const STEPS: { title: string; body: string }[] = [
  {
    title: 'Open the link in your invitation email',
    body: `It takes you to the VerityRx sign-in page. Type the email address the invitation was sent to and the temporary password from the email. The temporary password stops working after 7 days; if it has, ${contactPhrase()} and we will send a fresh one.`,
  },
  {
    title: 'Choose your own password',
    body: 'Use at least 12 characters, including an uppercase letter, a lowercase letter, a number, and a symbol. A password manager can create and save one.',
  },
  {
    title: 'Set up a code app',
    body: 'You’ll need a six-digit code each time you sign in. Open an authenticator app, scan the code on the sign-in page, then enter the six digits it shows.',
  },
  {
    // PM-40: the first full-access person also meets the terms; a reviewer
    // goes to the report shared with them.
    title: 'You are in',
    body: 'If nobody at your practice has accepted the terms yet, someone with full access reads and accepts them first. Then add your practice details. Reviewers go to the report shared with them.',
  },
];

const TROUBLE: { q: string; a: string }[] = [
  {
    q: 'The temporary password does not work',
    a: `It expires 7 days after the invitation was sent, and it must be typed exactly, including symbols. If it has expired, ${contactPhrase()} and we will send a new one.`,
  },
  {
    q: 'I lost or replaced my phone',
    a: `${capital(contactPhrase('your sign-in email address'))}. We will reset your sign-in so you can set up the code app on your new phone.`,
  },
  {
    q: 'I forgot my password',
    a: `If you have signed in before, use “Forgot your password?” on the sign-in page; a reset code is sent to your email. If you have never signed in, or your temporary password expired, that reset cannot help yet: ${contactPhrase()} and we will send a new invitation.`,
  },
  {
    q: 'I have two accounts, or I was invited twice',
    a: 'Use the address the invitation was sent to. One person can hold more than one role; the same sign-in reaches all of them.',
  },
];

function capital(s: string): string {
  return s.charAt(0).toUpperCase() + s.slice(1);
}

// The sign-in lattice (WOW3D) is its own chunk: the bundle every audience
// downloads first carries none of it, and the form paints before it loads.
const EntryBackdrop = lazy(() => import('../ui/three/EntryBackdrop').then((m) => ({ default: m.EntryBackdrop })));

export function Welcome() {
  const returnTo = safeReturnTo(new URLSearchParams(window.location.search).get('returnTo'));
  useSurfaceTheme();
  useDeviceViewport();
  useEffect(() => {
    document.title = 'Your first sign-in — VerityRx';
  }, []);
  return (
    <div className="vx">
      <main className="vx-gate vx-gate--auth vx-gate--welcome vx-stage">
        <Suspense fallback={null}>
          <EntryBackdrop />
        </Suspense>
        <div className="vx-gate__card vx-rise">
          <span className="vx-wordmark">VerityRx</span>
          <p className="vx-eyebrow" style={{ marginTop: 'var(--vx-8)' }}>
            Getting started
          </p>
          <h1>Your first sign-in</h1>
          <p className="vx-lead" style={{ marginTop: 'var(--vx-4)' }}>
            Four short steps, about two minutes. You only do the first three once.
          </p>
          <ol className="vx-welcome-steps">
            {STEPS.map((s, i) => (
              <li key={s.title}>
                <span className="vx-welcome-steps__n" aria-hidden="true">
                  {i + 1}
                </span>
                <div>
                  <h2>{s.title}</h2>
                  <p>{s.body}</p>
                </div>
              </li>
            ))}
          </ol>
          <section aria-labelledby="welcome-passkeys">
            <h2 className="vx-welcome-h2" id="welcome-passkeys">{PASSKEY_COPY.passkeyHeading}</h2>
            <p className="vx-small vx-muted">{PASSKEY_COPY.passkeyBody}</p>
            <p className="vx-small vx-muted">{PASSKEY_COPY.totpBody}</p>
          </section>
          <h2 className="vx-welcome-h2">If something goes wrong</h2>
          <dl className="vx-welcome-trouble">
            {TROUBLE.map((t) => (
              <div key={t.q}>
                <dt>{t.q}</dt>
                <dd>{t.a}</dd>
              </div>
            ))}
          </dl>
          <div className="vx-welcome-action" style={{ marginTop: 'var(--vx-8)' }}>
            <Link className="vx-btn vx-btn--primary" to={returnTo ?? '/'}>
              Go to sign in
            </Link>
          </div>
          <p className="vx-small" style={{ color: 'var(--vx-ink-4)', marginTop: 'var(--vx-8)' }} data-testid="welcome-help">
            {SIGNED_OUT_WORDS.lostInvitation()}
          </p>
        </div>
      </main>
    </div>
  );
}
